import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const GeometricRedTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#AE2012';
    const { fontFamily = 'font-sans', textColor = '#ffffff', backgroundColor = '#2B2D42', sectionBackgroundColor = '#2B2D42', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+2 434-232-534234' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'sarawalton@mail.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Main street # 32' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'sarawalton.com' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus aliquam orci a ex molestie, ornare odio scelerisque.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>
            {/* Left Sidebar - Dark */}
            <div data-cv-sidebar className="w-[35%] text-white p-8 flex flex-col overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                        {(photoSrc || showPlaceholders) && (
                            <div className="flex justify-center mb-6">
                                <div className="w-36 h-36 rounded-full border-[6px] overflow-hidden shadow-2xl" style={{ borderColor: imageBorderColor || primaryColor }}>
                                    {photoSrc ? (
                                        <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {(phone || email || address || website || showPlaceholders) && (
                            <div className="text-white p-6 mb-4 relative" style={{ backgroundColor: primaryColor }}>
                                <div className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2 rotate-45 w-8 h-8 bg-white z-10 shadow-[-4px_-4px_10px_rgba(0,0,0,0.1)]"></div>
                                <h2 className="text-lg font-black uppercase tracking-widest mb-5 flex items-center gap-3">
                                    {CV_LABELS.contact} <div className="h-px flex-1 bg-white/30"></div>
                                </h2>
                                <div className="cv-fs-contact space-y-4">
                                    {phone && (
                                        <div className="flex items-center gap-3">
                                            <Phone className="w-4 h-4 shrink-0" />
                                            <span className="text-xs font-bold">{phone}</span>
                                        </div>
                                    )}
                                    {email && (
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-4 h-4 shrink-0" />
                                            <span className="text-xs font-bold break-all">{email}</span>
                                        </div>
                                    )}
                                    {website && (
                                        <div className="flex items-center gap-3">
                                            <Globe className="w-4 h-4 shrink-0" />
                                            <span className="text-xs font-bold">{website}</span>
                                        </div>
                                    )}
                                    {address && (
                                        <div className="flex items-center gap-3">
                                            <MapPin className="w-4 h-4 shrink-0" />
                                            <span className="text-xs font-bold">{address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {summary && (
                            <div className="bg-[#001219] text-white p-6">
                                <h2 className="text-lg font-black uppercase tracking-widest mb-4 flex items-center gap-3">
                                    {CV_LABELS.profile} <div className="h-px flex-1 bg-white/10"></div>
                                </h2>
                                <p className="cv-fs-summary text-xs leading-relaxed text-slate-100 font-medium">
                                    {summary}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Right Content - White */}
            <div data-cv-main className="flex-1 min-h-0 overflow-hidden bg-white p-12 text-slate-800" style={{ backgroundColor: backgroundColor === '#2B2D42' ? '#ffffff' : backgroundColor, color: textColor === '#ffffff' ? '#1e293b' : textColor }}>
                {isContinuationPage ? (
                    <div className="mb-6 pb-3 border-b border-slate-200">
                        <p className="text-sm font-bold uppercase tracking-widest opacity-50">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <header className="mb-6 border-b-4 pb-6" style={{ borderColor: primaryColor }}>
                        <h1 className="cv-fs-name text-5xl font-black uppercase leading-none mb-2" style={{ color: primaryColor }}>
                            {data.personalInfo.firstName || (showPlaceholders ? 'JAME' : '')}{' '}
                            <span style={{ color: textColor === '#ffffff' ? '#1e293b' : textColor }}>
                                {data.personalInfo.lastName || (showPlaceholders ? 'SMITH' : '')}
                            </span>
                        </h1>
                        <p className="cv-fs-profession text-xl font-bold tracking-[0.2em] uppercase" style={{ color: sectionBackgroundColor === '#2B2D42' ? '#8D99AE' : sectionBackgroundColor }}>
                            {data.personalInfo.profession || (showPlaceholders ? 'BUSINESS MANAGER' : '')}
                        </p>
                    </header>
                )}

                {(data.experience.length > 0 || showPlaceholders) && (
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="bar" />
                        <div className="cv-fs-experience space-y-5">
                            {data.experience.length > 0 ? data.experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    className="pl-4 border-l-2"
                                    style={{ borderColor: `${primaryColor}40` }}
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                >
                                    <div className="flex justify-between items-baseline mb-1 gap-2">
                                        <h3 className="text-lg font-black uppercase" style={{ color: primaryColor }}>{exp.position}</h3>
                                        <span className="text-sm font-bold opacity-60 shrink-0">{exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}</span>
                                    </div>
                                    <p className="text-sm font-bold mb-2 opacity-80">{exp.company}</p>
                                    <p className="text-xs leading-relaxed opacity-70">
                                        {exp.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc scelerisque turpis sed tortor feugiat.'}
                                    </p>
                                </div>
                            )) : showPlaceholders ? (
                                <>
                                    <div className="pl-4 border-l-2" style={{ borderColor: `${primaryColor}40` }}>
                                        <div className="flex justify-between items-baseline mb-1">
                                            <h3 className="text-lg font-black uppercase" style={{ color: primaryColor }}>Business Manager</h3>
                                            <span className="text-sm font-bold opacity-60">2014 - {CV_LABELS.present}</span>
                                        </div>
                                        <p className="text-sm font-bold mb-2 opacity-80">Larana Inc.</p>
                                        <p className="text-xs leading-relaxed opacity-70">
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc scelerisque turpis sed tortor feugiat.
                                        </p>
                                    </div>
                                    <div className="pl-4 border-l-2" style={{ borderColor: `${primaryColor}40` }}>
                                        <div className="flex justify-between items-baseline mb-1">
                                            <h3 className="text-lg font-black uppercase" style={{ color: primaryColor }}>Business Analyst</h3>
                                            <span className="text-sm font-bold opacity-60">2010 - 2014</span>
                                        </div>
                                        <p className="text-sm font-bold mb-2 opacity-80">Borcelle Group</p>
                                        <p className="text-xs leading-relaxed opacity-70">
                                            Cras non ex vitae sem efficitur fermentum. Vivamus iaculis porta sollicitudin.
                                        </p>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </section>
                )}

                {(data.education.length > 0 || showPlaceholders) && (
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="bar" />
                        <div className="cv-fs-education space-y-4">
                            {data.education.length > 0 ? data.education.map((edu, i) => (
                                <div
                                    key={edu.id || i}
                                    className="pl-4 border-l-2"
                                    style={{ borderColor: `${primaryColor}40` }}
                                    data-cv-block
                                    data-cv-item-id={edu.id || undefined}
                                >
                                    <div className="flex justify-between items-baseline mb-1 gap-2">
                                        <h3 className="text-base font-black uppercase" style={{ color: primaryColor }}>
                                            {edu.degree || edu.institution}
                                        </h3>
                                        <span className="text-sm font-bold opacity-60 shrink-0">
                                            {edu.startDate?.slice?.(0, 4) || edu.startDate}
                                            {' - '}
                                            {edu.isCurrent ? CV_LABELS.present : edu.endDate?.slice?.(0, 4) || edu.endDate}
                                        </span>
                                    </div>
                                    <p className="text-sm font-bold mb-1 opacity-80">{edu.institution}</p>
                                    {edu.description ? (
                                        <p className="text-xs leading-relaxed opacity-70">{edu.description}</p>
                                    ) : null}
                                </div>
                            )) : showPlaceholders ? (
                                <div className="pl-4 border-l-2" style={{ borderColor: `${primaryColor}40` }}>
                                    <div className="flex justify-between items-baseline mb-1">
                                        <h3 className="text-base font-black uppercase" style={{ color: primaryColor }}>Diplôme</h3>
                                        <span className="text-sm font-bold opacity-60">2018 - 2022</span>
                                    </div>
                                    <p className="text-sm font-bold opacity-80">Établissement</p>
                                </div>
                            ) : null}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default GeometricRedTemplate;
